<template>
  <div :class="classList">
    <label v-if="label" class="form-label-btn"
      >{{ label.trim().replace(/\*$/, "") }}
      <span v-if="isRequired" class="asterisk-mark">*</span>
      <div class="setting-container" v-if="hasAddOptionBtn">
        <i
          class="material-icons ml-2 add-option-icon"
          @click="$emit('clickBtn', 'add-option')"
          v-tooltip="'Add Option'"
        >
          add_circle_outline
        </i>
      </div>
    </label>
    <div
      v-if="adminDescription && $store.getters.isAgent"
      class="admin-description"
    >
      <div v-html="adminDescription"></div>
    </div>
    <div v-if="description" class="description">
      <div v-html="description"></div>
    </div>
    <div class="row">
      <div
        :class="columnClass"
        v-for="(chunk, chunkIndex) of displayOptionList"
        :key="'chunk-' + chunkIndex"
      >
        <div
          class="form-check pl-4"
          v-for="(option, index) in chunk"
          :key="'option-' + optionsPerColumn * chunkIndex + index"
        >
          <input
            class="form-check-input"
            type="radio"
            :value="option"
            :id="getOptionId(optionsPerColumn * chunkIndex + index)"
            :checked="isCheck(option, optionsPerColumn * chunkIndex + index)"
            @change="
              handleOnChange(option, optionsPerColumn * chunkIndex + index)
            "
            :disabled="isDisabled"
          />
          <label
            v-if="isHtml"
            class="form-check-label ml-1"
            :for="getOptionId(optionsPerColumn * chunkIndex + index)"
            v-html="getDisplayOption(option)"
          >
          </label>
          <label
            v-else
            class="form-check-label ml-1"
            :for="getOptionId(optionsPerColumn * chunkIndex + index)"
          >
            {{ getDisplayOption(option) }}
          </label>
        </div>
        <div
          v-if="
            chunkIndex === displayOptionList.length - 1 &&
            hasOther &&
            isOtherSelected
          "
        >
          <input
            type="text"
            class="form-control"
            :value="getOtherValue"
            @input="handleOtherTextboxInput($event)"
          />
        </div>
      </div>
    </div>
    <div v-if="bottomDescription" class="description">
      <div v-html="bottomDescription"></div>
    </div>
    <p
      class="required-message"
      v-if="(value == null || value === '') && submitClicked && isRequired"
    >
      {{ getLabelByKeys("ERROR_MESSAGES.FIELD_REQUIRED") }}
    </p>
    <div
      class="button-group"
      v-if="listDisplayButtons && listDisplayButtons.length"
    >
      <button
        v-for="button of listDisplayButtons"
        :key="'button-' + button.id"
        @click="handleClickButton(button)"
        class="btn btn-secondary mr-1"
      >
        {{ getLabelByKeys(button.keyLangLabel) }}
      </button>
    </div>
  </div>
</template>
<script>
import { RADIO_OTHER_OPTIONS_PREFIX } from "../../../constants/prefix";
/* This component includes a label, and a radio group, which includes radio buttons and its label
Any error of the radio group (for example: required field) is showed in this component */
export default {
  name: "RadioGroup",
  props: {
    /* Name of the radio component, it is used in id of each radio button,
    therefore it need to be different between RadioGroup component */
    name: String,
    // Label at the top of component
    label: String,
    // Description which are only visible to admin
    adminDescription: String,
    // Description below label
    description: String,
    // Description below all options
    bottomDescription: String,
    // Input value, which is used for v-model
    value: [String, Boolean],
    // List of class to style for component, each class is separated by space
    classList: {
      type: String,
      default: "col-lg-12",
    },
    // List of options for radio buttons (the label of each radio buttons)
    optionList: {
      type: [Array, String],
      default: () => ["Yes", "No"],
    },
    hasOther: {
      type: Boolean,
      default: false,
    },
    // Index of default selected option
    defaultValue: [Number, String],
    // true if submit button is clicked, it is used to show 'required' error
    submitClicked: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    // field will be saved by boolean_selector
    isBooleanModel: {
      type: Boolean,
      default: false,
    },
    // Display options in multiple columns
    numberOfColumn: {
      type: Number,
      default: 1,
    },
    // Radio option display in HTML format
    isHtml: {
      type: Boolean,
      default: false,
    },
    // Map option list to display in multiple languages
    mapOptionListByLang: {
      type: Object,
      default: () => {},
    },
    buttonGroups: Array, // List of buttons attached by the end of multiselect
    builderField: Object,
  },
  created() {
    if (!this.isBooleanModel && !this.hasOther && this.value) {
      // Reset value if it is not included in option list
      if (!this.tryGet(() => this.builderField.usePredefinedOption)) {
        if (!this.normalizedOptionList.includes(this.value)) {
          this.$emit("input", "");
        }
      }
    }
    if (this.defaultValue != null && this.value == null) {
      this.$emit("input", this.normalizedOptionList[this.defaultValue]);
    }
  },
  // created() {
  //   this.$emit("input", this.normalizedOptionList[0]);
  // },
  computed: {
    hasAddOptionBtn() {
      if (!this.buttonGroups) {
        return false;
      }
      return this.buttonGroups.some((button) => button.id === "add-option");
    },
    listDisplayButtons() {
      if (!this.buttonGroups || !this.buttonGroups.length) {
        return null;
      }
      let result = this.buttonGroups.filter(
        (button) => !button.showIfSelect && button.id !== "add-option"
      );
      if (this.inputVal) {
        return result;
      }
      return result.filter((button) => !button.showIfSelect);
    },
    normalizedOptionList() {
      if (Array.isArray(this.optionList)) {
        return this.optionList;
      }
      return this.optionList
        .split("\n")
        .map((option) => option.trim())
        .filter((option) => option.length > 0);
    },
    displayOptionList() {
      if (!this.normalizedOptionList) {
        return [];
      }
      if (!this.numberOfColumn) {
        this.numberOfColumn = 1;
      }
      return this.splitArrayIntoChunks(
        this.normalizedOptionList,
        this.numberOfColumn
      );
    },
    getOtherValue() {
      if (!this.value) {
        return "";
      }
      if (!this.optionListExcludeOther.includes(this.value)) {
        return this.removeOtherRadioPrefix(this.value);
      }
    },
    optionListExcludeOther() {
      return this.normalizedOptionList.slice(0, -1);
    },
    optionsPerColumn() {
      if (this.normalizedOptionList == null) {
        return 0;
      }
      if (!this.numberOfColumn) {
        return this.normalizedOptionList.length;
      }
      return Math.ceil(this.normalizedOptionList.length / this.numberOfColumn);
    },
    columnClass() {
      if (this.numberOfColumn == null || this.numberOfColumn === 1) {
        return "col-lg-12";
      }
      return "col-lg-" + String(12 / this.numberOfColumn);
    },
    isOtherSelected() {
      if (!this.value) {
        return false;
      }
      if (!this.optionListExcludeOther.includes(this.value)) {
        return true;
      }
      return false;
    },
  },
  methods: {
    getOptionId(index) {
      return `Radion-Option-${this._uid}-${this.name}-${index}`;
    },
    isCheck(option, index) {
      if (this.isBooleanModel) {
        if (this.value === true) {
          return option === "Yes";
        } else if (this.value === false) {
          return option === "No";
        }
      } else {
        if (this.hasOther) {
          if (index === this.normalizedOptionList.length - 1) {
            return this.isOtherSelected;
          }
        }
        return option === this.value;
      }
    },
    handleOnChange(option, index) {
      console.log("onChange", option, index);
      if (this.isBooleanModel) {
        this.$emit("input", option === "Yes");
      } else {
        if (this.hasOther && index === this.normalizedOptionList.length - 1) {
          this.$emit("input", RADIO_OTHER_OPTIONS_PREFIX);
        } else {
          this.$emit("input", option);
        }
      }
    },
    handleOtherTextboxInput(event) {
      this.$emit("input", RADIO_OTHER_OPTIONS_PREFIX + event.target.value);
    },
    getDisplayOption(option) {
      if (!this.mapOptionListByLang) {
        return option;
      }
      if (this.mapOptionListByLang[option]) {
        return this.mapOptionListByLang[option];
      }
      return option;
    },
    handleClickButton(button) {
      this.$emit("clickBtn", button.id);
    },
  },
};
</script>




// WEBPACK FOOTER //
// src/components/common/form-elements/RadioGroup.vue